import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { StickyNote, UsersRound } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from 'react'

export type CompactPlaySidebarPanel = 'players' | 'notes' | null

interface CompactPlaySidebarProps {
  activePanel: CompactPlaySidebarPanel
  onActivePanelChange: (panel: CompactPlaySidebarPanel) => void
  players: ReactNode
  notes: ReactNode
}

const panelWidth = 'min(22rem, calc(100vw - 3rem))'

export function CompactPlaySidebar({
  activePanel,
  onActivePanelChange,
  players,
  notes,
}: CompactPlaySidebarProps) {
  const { language, t } = useI18n()
  const direction = language === 'ar' ? 'rtl' : 'ltr'
  const isOpen = activePanel !== null
  const railRef = useRef<HTMLDivElement>(null)
  const playersTabRef = useRef<HTMLButtonElement>(null)
  const notesTabRef = useRef<HTMLButtonElement>(null)
  const previousPanelRef = useRef<CompactPlaySidebarPanel>(activePanel)
  const [viewportInsets, setViewportInsets] = useState({ top: 0, bottom: 0 })

  useEffect(() => {
    const viewport = window.visualViewport
    const updateInsets = () => {
      const top = Math.max(0, viewport?.offsetTop ?? 0)
      const bottom = viewport
        ? Math.max(0, window.innerHeight - viewport.offsetTop - viewport.height)
        : 0
      setViewportInsets(current =>
        current.top === top && current.bottom === bottom
          ? current
          : { top, bottom },
      )
    }

    updateInsets()
    viewport?.addEventListener('resize', updateInsets)
    viewport?.addEventListener('scroll', updateInsets)
    window.addEventListener('resize', updateInsets)
    return () => {
      viewport?.removeEventListener('resize', updateInsets)
      viewport?.removeEventListener('scroll', updateInsets)
      window.removeEventListener('resize', updateInsets)
    }
  }, [])

  const closePanel = useCallback(() => {
    onActivePanelChange(null)
  }, [onActivePanelChange])

  useEffect(() => {
    const previous = previousPanelRef.current
    previousPanelRef.current = activePanel
    if (previous === activePanel)
      return

    const tab
      = (activePanel ?? previous) === 'players'
        ? playersTabRef.current
        : notesTabRef.current
    const frame = window.requestAnimationFrame(() => {
      tab?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [activePanel])

  useEffect(() => {
    if (!isOpen)
      return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closePanel()
        return
      }
      if (event.key !== 'Tab')
        return

      const focusable = [
        ...(railRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter(element => !element.closest('[hidden], [aria-hidden="true"]'))
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last)
        return

      const focused = document.activeElement
      if (
        event.shiftKey
        && (focused === first || !focusable.includes(focused as HTMLElement))
      ) {
        event.preventDefault()
        last.focus()
      }
      else if (
        !event.shiftKey
        && (focused === last || !focusable.includes(focused as HTMLElement))
      ) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closePanel, isOpen])

  const togglePanel = (panel: Exclude<CompactPlaySidebarPanel, null>) => {
    onActivePanelChange(activePanel === panel ? null : panel)
  }

  const railStyle = {
    top: Math.max(76, viewportInsets.top + 12),
    bottom: `calc(${viewportInsets.bottom + 12}px + env(safe-area-inset-bottom))`,
  } as CSSProperties
  const drawerStyle = {
    '--compact-play-panel-width': panelWidth,
    'width': 'calc(var(--compact-play-panel-width) + 3rem)',
    'transform': isOpen
      ? 'translateX(0)'
      : 'translateX(var(--compact-play-panel-width))',
  } as CSSProperties

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/35 backdrop-blur-[1px]"
          aria-label={t('common.close')}
          tabIndex={-1}
          data-testid="compact-play-sidebar-backdrop"
          onClick={closePanel}
        />
      )}

      <div
        ref={railRef}
        dir="ltr"
        className="pointer-events-none fixed inset-x-0 z-[45] overflow-hidden"
        style={railStyle}
        data-testid="compact-play-sidebar"
      >
        <div
          className="absolute inset-y-0 right-0 flex transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={drawerStyle}
          data-state={isOpen ? 'open' : 'closed'}
          role={isOpen ? 'dialog' : undefined}
          aria-modal={isOpen ? true : undefined}
          aria-label={
            isOpen
              ? activePanel === 'players'
                ? t('play.playerList')
                : t('play.draftNotes')
              : undefined
          }
        >
          <div className="relative w-12 shrink-0">
            <div
              dir={direction}
              className="pointer-events-auto absolute right-0 top-1/2 flex max-h-full -translate-y-1/2 flex-col gap-2 overflow-y-auto"
            >
              <SidebarTab
                buttonRef={playersTabRef}
                id="compact-play-players-tab"
                controls="compact-play-players-panel"
                label={t('play.playerList')}
                active={activePanel === 'players'}
                icon={<UsersRound className="size-4" aria-hidden="true" />}
                onClick={() => togglePanel('players')}
              />
              <SidebarTab
                buttonRef={notesTabRef}
                id="compact-play-notes-tab"
                controls="compact-play-notes-panel"
                label={t('play.draftNotes')}
                active={activePanel === 'notes'}
                icon={<StickyNote className="size-4" aria-hidden="true" />}
                onClick={() => togglePanel('notes')}
              />
            </div>
          </div>

          {isOpen && (
            <aside
              dir={direction}
              className="pointer-events-auto min-h-0 shrink-0 overflow-hidden rounded-l-xl border border-r-0 border-border/70 bg-background/95 p-2 shadow-2xl backdrop-blur-md dark:border-[#a88a5d]/70 dark:bg-[#111d29]/95"
              style={{ width: 'var(--compact-play-panel-width)' }}
              data-testid="compact-play-sidebar-panel"
            >
              <div
                id="compact-play-players-panel"
                role="region"
                aria-labelledby="compact-play-players-tab"
                className={cn(
                  'h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-contain',
                  activePanel === 'players' ? 'flex' : 'hidden',
                )}
                hidden={activePanel !== 'players'}
              >
                {players}
              </div>
              <div
                id="compact-play-notes-panel"
                role="region"
                aria-labelledby="compact-play-notes-tab"
                className={cn(
                  'h-full min-h-0 flex-col',
                  activePanel === 'notes' ? 'flex' : 'hidden',
                )}
                hidden={activePanel !== 'notes'}
              >
                {notes}
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  )
}

function SidebarTab({
  buttonRef,
  id,
  controls,
  label,
  active,
  icon,
  onClick,
}: {
  buttonRef: Ref<HTMLButtonElement>
  id: string
  controls: string
  label: string
  active: boolean
  icon: ReactNode
  onClick: () => void
}) {
  return (
    <Button
      ref={buttonRef}
      id={id}
      type="button"
      variant={active ? 'default' : 'outline'}
      className={cn(
        'h-auto min-h-16 w-12 flex-col gap-1 rounded-l-xl rounded-r-none border-r-0 px-1 py-2 shadow-lg has-[>svg]:px-1',
        !active && 'bg-background/95 backdrop-blur-md dark:border-[#a88a5d]/70 dark:bg-[#111d29]/95 dark:text-[#ffe4ab]',
      )}
      aria-label={label}
      aria-expanded={active}
      aria-controls={controls}
      data-testid={id}
      onClick={onClick}
    >
      {icon}
      <span
        className="max-w-10 whitespace-normal text-center text-[10px] leading-tight"
        aria-hidden="true"
      >
        {label}
      </span>
    </Button>
  )
}
